import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { Button, Form, Typography } from 'antd';

import messages from '../../constants/messages';

import { useSetState } from '../../hooks/useSetState';

import { resetPassword, verifyToken } from '../../redux/actions/user';

import PasswordField from '../../components/PasswordField';
import { errorToastHandler } from '../../components/action_notifier';

import AuthLayout from '../../layouts/AuthLayout';

const { Title } = Typography;

function ResetPassword() {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { token } = useParams();

  const [isFormSubmitted, setIsFormSubmitted] = useSetState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [tokenVerificationErrorMsg, setTokenVerificationErrorMsg] = useState('');

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable?.(true))
      .catch(() => setSubmittable?.(false));
  }, [values]);

  useEffect(() => {
    try {
      dispatch(verifyToken({ token }))
        .then(() => setIsTokenValid(true))
        .catch(error => errorToastHandler(error?.response?.data?.message || messages.DEFAULT_ERROR));
    } catch (error) {
      setTokenVerificationErrorMsg(error?.response.data.message);
    }
  }, [dispatch]);

  async function reset(event) {
    event.preventDefault();

    try {
      const { password } = await form.validateFields();

      await dispatch(resetPassword({ token, password }));

      form.resetFields();
      setIsFormSubmitted(true);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  return (
    <AuthLayout asideContent={{ text: 'We’ll get you back on the Kraal platform shortly.' }}>
      <Title className="mb-4" level={3}>
        Reset Password
      </Title>
      {isTokenValid && !isFormSubmitted && (
        <Form
          form={form}
          className="mt-3"
          layout="vertical"
          requiredMark={false}
          size="small"
          initialValues={{ password: null, password_confirm: null }}>
          <PasswordField
            name="password"
            label="New Password"
            rules={[
              { required: true, message: messages.FIELD_REQUIRED('Password') },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                message: messages.PASSWORD_INVALID
              }
            ]}
          />

          <PasswordField
            dependencies={['password']}
            label="New Password Confirmation"
            name="password_confirm"
            rules={[
              {
                required: true,
                message: messages.FIELD_REQUIRED('Confirm Password')
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return getFieldValue('password') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error(messages.PASSWORDS_MISMATCH));
                }
              })
            ]}
          />

          <Button className="w-100 mt-4" size="large" type="primary" disabled={!submittable} onClick={reset}>
            Reset Password
          </Button>
        </Form>
      )}
      {!isTokenValid && (
        <div className="pt-3">
          <p className="text-red">{tokenVerificationErrorMsg}</p>
          <Link to="/auth/recover-pwd">Get new token</Link>
        </div>
      )}
      {isFormSubmitted && (
        <div className="pt-3">
          <p>Your password has been reset. You can now login with your new password</p>
          <Link to="/auth/login">Go to Login</Link>
        </div>
      )}
    </AuthLayout>
  );
}

export default ResetPassword;
